import { derived, type Readable } from 'svelte/store';
import type { JsonValue } from '@protobuf-ts/runtime';
import { formatDistance } from 'date-fns';
import type { ValueOf } from 'type-fest';
import { z } from 'zod';

import type { DialConf } from '@viamrobotics/sdk';

import type { APIRobotPartWithoutConfig } from '$lib/api/robots';

import type { AppEnvironment } from './api/environment';
import { createGetLocationQuery } from './api/locations';
import type { PartID } from './robot-config';
import type { PartRow } from '../routes/(auth-required)/fleet/machines/part-row';
import type { APIMachinePart } from './api/data-management';
import type { Robot as Machine } from '@viamrobotics/web-client/app/v1/app';
import { timestampToDate } from './api';

export interface PartSummary {
  id: string;
  name: string;
  dnsName: string;
  isMain: boolean;
  remoteAddress: string;
  state: OnlineState;
  info: PartInfo;
}

export type OnlineState =
  | OnlineStateLive
  | OnlineStateOffline
  | OnlineStateAwaitingSetup;

export type RDKVersion = { tag: string } | { revision: string };

export interface AgentVersion {
  type: 'major' | 'minor';
  version: string;
}

export interface PartInfo {
  os?: string | undefined;
  platform?: string | undefined;
  rdkVersion?: RDKVersion | undefined;
  agentVersion?: AgentVersion | undefined;
  agentVersionRunning?: AgentVersion | undefined;
  hostname?: string | undefined;
  ipAddresses?: string[] | undefined;
}

export interface OnlineStateAwaitingSetup {
  status: typeof OnlineStatuses.AWAITING_SETUP;
}

export interface OnlineStateLive {
  status: typeof OnlineStatuses.LIVE;
  lastOnlineDate: Date | undefined;
}

export interface OnlineStateOffline {
  status: typeof OnlineStatuses.OFFLINE;
  lastOnline: string;
  lastOnlineDate: Date | undefined;
}

export interface OnlineStateAwaitingSetup {
  status: typeof OnlineStatuses.AWAITING_SETUP;
}

export const OnlineStatuses = {
  LIVE: 'live',
  OFFLINE: 'offline',
  AWAITING_SETUP: 'awaiting setup',
} as const;

export type OnlineStatus = ValueOf<typeof OnlineStatuses>;

export const API_TO_ONLINE_STATUS: Record<number, OnlineStatus> = {
  1: OnlineStatuses.LIVE,
  2: OnlineStatuses.OFFLINE,
  3: OnlineStatuses.AWAITING_SETUP,
  0: OnlineStatuses.AWAITING_SETUP, // UNSPECIFIED defaults to AWAITING_SETUP
};

/**
 * Map a part object from the API to a shape convenient for the UI.
 *
 * @param apiPart The part returned from the API
 * @returns A summary of the part, with status, to drive the UI
 */
export const toPartSummary = (
  apiPart: APIRobotPartWithoutConfig
): PartSummary => {
  const {
    id,
    name,
    dnsName,
    mainPart: isMain,
    fqdn: remoteAddress,
    lastAccess,
    onlineState,
    secondsSinceOnline,
    userSuppliedInfo: rawUserSuppliedInfo,
  } = apiPart;
  const userSuppliedInfo = toUserSuppliedInfo(rawUserSuppliedInfo);
  const {
    os,
    platform,
    hostname,
    ipAddresses,
    version,
    gitRevision,
    agentSubsystemVersions,
  } = userSuppliedInfo;
  const { viamAgent, viamAgentRunning } = agentSubsystemVersions;

  let rdkVersion: PartInfo['rdkVersion'] = undefined;
  if (version) {
    rdkVersion = { tag: version };
  } else if (gitRevision) {
    rdkVersion = { revision: gitRevision };
  }

  let agentVersion: PartInfo['agentVersion'] = undefined;
  if (viamAgent === 'customURL') {
    agentVersion = {
      type: 'minor',
      version: 'pin_url',
    };
  } else if (viamAgent) {
    agentVersion = {
      type: 'major',
      version: viamAgent,
    };
  }

  const agentVersionRunning: PartInfo['agentVersionRunning'] = viamAgentRunning
    ? { type: 'major', version: viamAgentRunning }
    : agentVersion;

  const info: PartInfo = {
    rdkVersion,
    agentVersion,
    agentVersionRunning,
    os,
    platform,
    hostname,
    ipAddresses,
  };

  const status = API_TO_ONLINE_STATUS[onlineState];
  const state = apiStateToOnlineState(status, secondsSinceOnline, lastAccess);

  return { id, name, dnsName, isMain, remoteAddress, state, info };
};

/**
 * Converts an APIMachinePart object to an OnlineState.
 *
 * @param part - The machine part data from the API
 * @returns A structured OnlineState object indicating the current online status of the machine part
 */
export const machinePartToOnlineState = (part: APIMachinePart): OnlineState => {
  if (part.lastOnline === undefined) {
    return {
      status: OnlineStatuses.AWAITING_SETUP,
    };
  }

  return apiStateToOnlineState(
    part.onlineState,
    part.secondsSinceOnline,
    part.lastOnline
  );
};

/**
 * Converts a PartRow object to an OnlineState.
 * Handles timestamp conversion from the PartRow's seconds/nanos format to a JavaScript Date.
 *
 * @param partRow - The part row data, typically from a database query result
 * @returns A structured OnlineState object indicating the current online status of the part
 */
export const partRowToOnlineState = (partRow: PartRow): OnlineState => {
  if (partRow.lastAccess === undefined) {
    return {
      status: OnlineStatuses.AWAITING_SETUP,
    };
  }

  const lastOnlineDate = timestampToDate(partRow.lastAccess);

  return apiStateToOnlineState(
    API_TO_ONLINE_STATUS[partRow.onlineState],
    partRow.secondsSinceOnline,
    lastOnlineDate
  );
};

/**
 * Converts a Machine object to an OnlineState.
 * Handles timestamp conversion from the Machine's seconds/nanos format to a JavaScript Date.
 *
 * @param machine - The machine data
 * @returns A structured OnlineState object indicating the current online status of the machine
 */
export const machineToOnlineState = (machine: Machine): OnlineState => {
  if (machine.lastAccess === undefined) {
    return {
      status: OnlineStatuses.AWAITING_SETUP,
    };
  }

  const lastOnlineDate = timestampToDate(machine.lastAccess);

  return apiStateToOnlineState(
    API_TO_ONLINE_STATUS[machine.onlineState],
    machine.secondsSinceOnline,
    lastOnlineDate
  );
};

/**
 * Converts an API online state string into a structured OnlineState object.
 *
 * @param onlineState - The raw online status from the API (e.g., 'live', 'offline', 'awaiting_setup')
 * @param lastOnlineText - Human-readable text describing when the entity was last online (e.g., '2 hours ago')
 * @param lastAccess - The timestamp of the last access/online time
 * @returns A structured OnlineState object with status and relevant metadata
 */
export const apiStateToOnlineState = (
  onlineState: OnlineStatus | undefined,
  secondsSinceOnline: string,
  lastAccess: Date | undefined
): OnlineState => {
  if (lastAccess === undefined || onlineState === undefined) {
    return {
      status: OnlineStatuses.AWAITING_SETUP,
    };
  }
  switch (onlineState) {
    case OnlineStatuses.LIVE: {
      return {
        status: OnlineStatuses.LIVE,
        lastOnlineDate: lastAccess,
      };
    }
    case OnlineStatuses.OFFLINE: {
      return {
        status: OnlineStatuses.OFFLINE,
        lastOnlineDate: lastAccess,
        lastOnline: formatLastOnline(secondsSinceOnline),
      };
    }
    case OnlineStatuses.AWAITING_SETUP: {
      return {
        status: OnlineStatuses.AWAITING_SETUP,
      };
    }
    default: {
      // Fallback to awaiting setup for unknown states
      return {
        status: OnlineStatuses.AWAITING_SETUP,
      };
    }
  }
};

/**
 * Converts a duration (in seconds) into a human-readable "time ago" string.
 *
 * @param secondsSinceOnline - Number of seconds elapsed since the entity was last online (as a string)
 * @returns Human-readable relative time (e.g., "2 hours ago", "just now") or "unknown" if invalid
 */
export const formatLastOnline = (secondsSinceOnline: string): string => {
  try {
    const numSeconds = Number(secondsSinceOnline);

    if (!Number.isFinite(numSeconds) || numSeconds < 0) {
      return 'unknown';
    }

    // Calculate the past timestamp by subtracting the elapsed time from now
    const pastDate = new Date(Date.now() - numSeconds * 1000);
    const now = new Date();

    return formatDistance(pastDate, now, { addSuffix: true });
  } catch {
    return 'unknown';
  }
};

/**
 * isInPrimaryOrg is used to determine if the org-id used to access the robot
 * is the primary organization for the given location.
 *
 * This is useful for disabling certain menu items that only make sense when you are
 * a locations primary owner
 *
 * @param currentLocationID locationID of interest
 * @param currentOrgID orgID of interest
 * @returns A derived store that returns a boolean
 */
export const isInPrimaryOrg = (
  currentLocationID: Readable<string | undefined>,
  currentOrgID: Readable<string | undefined>
): Readable<boolean> => {
  const locationQuery = createGetLocationQuery(currentLocationID);
  return derived(
    [locationQuery, currentOrgID],
    ([$locationQuery, $currentOrgID]) =>
      $locationQuery.isSuccess &&
      Boolean(
        $locationQuery.data?.organizations.some(
          (org) => org.primary && org.organizationId === $currentOrgID
        )
      )
  );
};

/**
 * dialConfigsForParts transforms the part summaries into the format used
 * by the typescript and svelte sdks to establish a connection
 * to the live robot part.
 *
 * It will only return parts that have a "live" status.
 */
export const dialConfigsForParts = (
  parts: PartSummary[] | undefined,
  environment: AppEnvironment | undefined,
  accessToken?: string,
  returnForOfflineParts?: boolean
): Record<PartID, DialConf> => {
  const signalingAddress = environment?.signalingAddress;
  if (!parts || !signalingAddress) {
    return {};
  }
  return Object.fromEntries(
    parts
      .filter((part) => part.state.status === 'live' || returnForOfflineParts)
      .map((part) => [
        part.id,
        {
          host: part.remoteAddress,
          signalingAddress,
          // TODO: maxConnectionAttempts?
          // NOTE(RSDK-9165): viam-micro-rdk does not support sessions
          // disableSessions: part.info.platform === 'esp32',
          // TODO(APP-6893): sessions causing prod issue
          disableSessions: true,
          extraHeaders: {
            viam_client: 'viam-app',
          },
          ...(accessToken && {
            credentials: {
              type: 'access-token' as const,
              payload: accessToken,
            },
          }),
        },
      ])
  );
};

export interface UserSuppliedInfo extends Record<string, unknown> {
  os?: string | undefined;
  platform?: string | undefined;
  version?: string | undefined;
  gitRevision?: string | undefined;
  hostname?: string | undefined;
  ipAddresses?: string[] | undefined;
  agentSubsystemVersions: AgentSubsystemVersions;
}

export interface AgentSubsystemVersions {
  agentSysCfg?: string | undefined;
  viamAgent?: string | undefined;
  viamAgentRunning?: string | undefined;
  viamServer?: string | undefined;
  agentProvisioning?: string | undefined;
}

const toAgentSubsystemVersions = (
  rawVersions: RawAgentSubsystemVersions | undefined
): AgentSubsystemVersions => {
  if (rawVersions === undefined) {
    return {};
  }
  const {
    'agent-syscfg': agentSysCfg,
    'viam-agent': viamAgent,
    'viam-agent_running': viamAgentRunning,
    'viam-server': viamServer,
    'agent-provisioning': agentProvisioning,
  } = rawVersions;
  return {
    agentSysCfg,
    viamAgent,
    viamAgentRunning,
    viamServer,
    agentProvisioning,
  };
};

export const toUserSuppliedInfo = (
  rawUserSuppliedInfo: JsonValue | undefined
): UserSuppliedInfo => {
  const parseResult = UserSuppliedInfoSchema.safeParse(
    rawUserSuppliedInfo ?? {}
  );
  if (!parseResult.success) {
    return {
      agentSubsystemVersions: {},
    };
  }
  const {
    agent_subsystem_versions: rawVersions,
    os,
    platform,
    host: hostname,
    ips: ipAddresses,
    version,
    git_revision: gitRevision,
    ...userSuppliedInfo
  } = parseResult.data;

  return {
    ...userSuppliedInfo,
    os,
    platform,
    hostname,
    ipAddresses,
    version,
    gitRevision,
    agentSubsystemVersions: toAgentSubsystemVersions(rawVersions),
  };
};

type RawAgentSubsystemVersions = z.infer<typeof AgentSubsystemVersionsSchema>;

const AgentSubsystemVersionsSchema = z
  .object({
    'agent-syscfg': z.string(),
    'viam-agent': z.string(),
    'viam-agent_running': z.string(),
    'viam-server': z.string(),
    'agent-provisioning': z.string(),
  })
  .partial()
  .passthrough();

const UserSuppliedInfoSchema = z
  .object({
    os: z.string(),
    platform: z.string(),
    host: z.string(),
    ips: z.array(z.string()),
    version: z.string(),
    git_revision: z.string(),
    agent_subsystem_versions: AgentSubsystemVersionsSchema,
  })
  .partial()
  .passthrough();
